/**
 * Prod-auth token provider (real login against the prod IdP).
 *
 * Oxbo-M (`m.oxbo.energy`) requires `Authorization: Bearer <JWT>` on every data
 * call. Unlike the dev-auth provider — which mints a token from the unguarded
 * `_dev/mint` endpoint — this module performs a REAL login against the prod
 * identity provider (`auth.qa.oxbo.energy`) using a demo user's email +
 * password, holds the access token in memory for the mApi baseQuery to read,
 * and keeps it fresh:
 *
 *   1. On start it POSTs `${VITE_OXBO_AUTH_URL}/v1/auth/login` with the demo
 *      credentials and stores the returned access + refresh tokens.
 *   2. A short margin before the access token's `exp` it rotates via
 *      `${VITE_OXBO_AUTH_URL}/v1/auth/refresh` (rotating refresh token).
 *   3. If refresh fails (revoked/expired refresh token), it falls back to a
 *      full re-login with the demo credentials, so the app never gets stuck
 *      token-less.
 *
 * It is gated entirely behind `VITE_PROD_AUTH=true`. When unset/false none of
 * this runs (the dev-auth provider and/or MSW cover the other build modes).
 *
 * Oxbo-P (pApi) needs no token — `OXBO_P_AUTH_REQUIRED=false` — so it never
 * consults this module.
 *
 * Demo credentials come from the build env (`VITE_DEMO_EMAIL` /
 * `VITE_DEMO_PASSWORD`); they are NOT hard-coded here.
 */

/** Refresh this many ms before the token's expiry so a call never races exp. */
const REFRESH_LEAD_MS = 2 * 60 * 1000; // 2 min
/** Fallback access-token lifetime if the JWT carries no readable `exp`. */
const FALLBACK_LIFETIME_MS = 15 * 60 * 1000;
/** Assume at least this much runway so a bad clock can't schedule exp in the past. */
const MIN_LIFETIME_MS = 60 * 1000;
/** Back-off before retrying a failed login. */
const RETRY_DELAY_MS = 5 * 1000;
/**
 * Longer back-off after a rate-limit (429). The login endpoint caps at ~10/15min
 * per IP; on a live demo, a hard reload re-instantiates this module, so without
 * the sessionStorage cache below every reload burns a login and quickly 429s.
 * When we do hit a 429, wait out most of the window instead of hammering it.
 */
const RATE_LIMIT_DELAY_MS = 60 * 1000;
/** sessionStorage key for the persisted token pair (survives a page reload). */
const STORAGE_KEY = "oxbo.prodAuth.v1";
/**
 * localStorage key: when "1" the user has deliberately logged out, so the app
 * must NOT auto-log-in the demo user. Persisted in localStorage (not session)
 * so the choice survives a full tab close/reopen — a logout should stick until
 * the user explicitly logs back in or picks "continue as demo".
 */
const MANUAL_LOGOUT_KEY = "oxbo.manualLogout";

function logoutUrl(): string {
  return `${authBase()}/v1/auth/logout`;
}

/** localStorage handle, or null when unavailable (SSR / privacy mode). */
function localStore(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** True when the user has deliberately logged out (manual-logout flag set). */
export function isManualLogout(): boolean {
  try {
    return localStore()?.getItem(MANUAL_LOGOUT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Set or clear the manual-logout flag. */
function setManualLogout(on: boolean): void {
  const s = localStore();
  if (!s) return;
  try {
    if (on) s.setItem(MANUAL_LOGOUT_KEY, "1");
    else s.removeItem(MANUAL_LOGOUT_KEY);
  } catch {
    /* quota / disabled — in-memory token state still governs this session */
  }
}

/** Clear the manual-logout flag (public: the demo-fallback / login paths). */
export function clearManualLogout(): void {
  setManualLogout(false);
}

/** Whether prod-auth is active for this build. */
export function isProdAuthEnabled(): boolean {
  return import.meta.env.VITE_PROD_AUTH === "true";
}

function authBase(): string {
  return import.meta.env.VITE_OXBO_AUTH_URL ?? "/api/auth";
}

function loginUrl(): string {
  return `${authBase()}/v1/auth/login`;
}

function refreshUrl(): string {
  return `${authBase()}/v1/auth/refresh`;
}

function demoEmail(): string {
  return import.meta.env.VITE_DEMO_EMAIL ?? "";
}

function demoPassword(): string {
  return import.meta.env.VITE_DEMO_PASSWORD ?? "";
}

let accessToken: string | null = null;
let refreshToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<string | null> | null = null;

/** The current prod access token, or null if none has been obtained yet. */
export function getProdToken(): string | null {
  return accessToken;
}

/** sessionStorage handle, or null when unavailable (SSR / privacy mode). */
function store(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Persist the token pair so a page reload reuses it instead of re-logging-in. */
function persist(): void {
  const s = store();
  if (!s) return;
  try {
    if (accessToken) {
      s.setItem(STORAGE_KEY, JSON.stringify({ access_token: accessToken, refresh_token: refreshToken }));
    } else {
      s.removeItem(STORAGE_KEY);
    }
  } catch {
    /* quota / disabled — in-memory still works, just no reload reuse */
  }
}

/** Restore a persisted token pair into memory (does not validate). */
function restorePersisted(): void {
  const s = store();
  if (!s) return;
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return;
    const pair = JSON.parse(raw) as TokenPair;
    accessToken = pair.access_token ?? null;
    refreshToken = pair.refresh_token ?? null;
  } catch {
    /* corrupt entry — ignore, a fresh login will replace it */
  }
}

/**
 * True when `token` is present and not within the refresh lead-time of expiry —
 * i.e. safe to use as-is without a network round-trip. Tokens with no readable
 * `exp` are treated as usable (the scheduled refresh still covers them).
 */
function isTokenFresh(token: string | null): boolean {
  if (!token) return false;
  const expMs = readExpMs(token);
  if (expMs == null) return true;
  return expMs - Date.now() > REFRESH_LEAD_MS;
}

/** Decode an arbitrary claim map from a JWT payload, or null if unreadable. */
function decodeClaims(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * The signed-in user's email, decoded from the access token (the `email`
 * claim, falling back to `sub` if that is an email). Null when token-less or
 * unreadable — shown next to the logout control for display only.
 */
export function getProdEmail(): string | null {
  if (!accessToken) return null;
  const claims = decodeClaims(accessToken);
  if (!claims) return null;
  const email = claims.email;
  if (typeof email === "string" && email) return email;
  const sub = claims.sub;
  if (typeof sub === "string" && sub.includes("@")) return sub;
  return null;
}

/** The `org_roles` claim of the current access token (empty if none). */
export function getProdOrgRoles(): string[] {
  if (!accessToken) return [];
  const claims = decodeClaims(accessToken);
  const roles = claims?.org_roles;
  return Array.isArray(roles) ? roles.filter((r): r is string => typeof r === "string") : [];
}

/** The current token's `org_id` claim (the active org UUID), or null. Used by
 * the ACL admin screen as the `organization_id` for grant/revoke. */
export function getProdOrgId(): string | null {
  if (!accessToken) return null;
  const claims = decodeClaims(accessToken);
  const orgId = claims?.org_id;
  return typeof orgId === "string" && orgId ? orgId : null;
}

/** The current token's `sub` claim (the caller's own user_id UUID), or null. */
export function getProdUserId(): string | null {
  if (!accessToken) return null;
  const claims = decodeClaims(accessToken);
  const sub = claims?.sub;
  return typeof sub === "string" && sub ? sub : null;
}

/** Decode a JWT's `exp` (seconds) → absolute ms, or null if unreadable. */
function readExpMs(token: string): number | null {
  const claims = decodeClaims(token);
  const exp = claims?.exp;
  return typeof exp === "number" ? exp * 1000 : null;
}

interface TokenPair {
  access_token?: string;
  refresh_token?: string;
}

/** An auth-request error that carries the HTTP status (so we can back off on 429). */
class AuthError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** Full login with the given credentials. Throws on any non-ok response. */
async function loginWith(email: string, password: string): Promise<TokenPair> {
  if (!email || !password) {
    throw new Error("prod-auth: email / password are required");
  }
  const res = await fetch(loginUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    throw new AuthError(`prod-auth login failed: ${res.status} ${res.statusText}`, res.status);
  }
  const body = (await res.json()) as TokenPair;
  if (!body.access_token) {
    throw new Error("prod-auth login returned no access_token");
  }
  return body;
}

/** Full login with the demo credentials (the default auto-login path). */
async function loginOnce(): Promise<TokenPair> {
  const email = demoEmail();
  const password = demoPassword();
  if (!email || !password) {
    throw new Error("prod-auth: VITE_DEMO_EMAIL / VITE_DEMO_PASSWORD not set");
  }
  return loginWith(email, password);
}

/**
 * Log in with explicit user credentials (the interactive login screen). On
 * success this stores the token pair, arms the refresh loop, and CLEARS the
 * manual-logout flag so the app renders normally and keeps the session fresh.
 * Throws on failure (the caller surfaces the error) and leaves prior state
 * untouched. The manual-logout flag is cleared BEFORE the request so a reload
 * mid-flight cannot wedge on the login screen; it is only cleared, never set,
 * here.
 */
export async function loginProdWith(email: string, password: string): Promise<void> {
  const pair = await loginWith(email, password);
  storePair(pair);
  clearManualLogout();
}

/**
 * Explicit "continue as demo" — the ONLY path that logs in the demo user now.
 * Auto/background demo login was removed (init + renew never mint a demo token),
 * so the app can no longer silently become demo; a visitor who wants the demo
 * presses the button on the login screen, which calls this. Logs in with the
 * build-time demo credentials, stores the pair, and clears the manual-logout
 * flag so the render-gate shows the app.
 */
export async function loginAsDemo(): Promise<void> {
  const pair = await loginOnce();
  storePair(pair);
  clearManualLogout();
}

/** Rotate the refresh token → new pair. Throws if no refresh token or non-ok. */
async function refreshOnce(): Promise<TokenPair> {
  if (!refreshToken) throw new Error("prod-auth: no refresh token to rotate");
  const res = await fetch(refreshUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!res.ok) {
    throw new AuthError(`prod-auth refresh failed: ${res.status} ${res.statusText}`, res.status);
  }
  const body = (await res.json()) as TokenPair;
  if (!body.access_token) {
    throw new Error("prod-auth refresh returned no access_token");
  }
  return body;
}

function storePair(pair: TokenPair): string {
  accessToken = pair.access_token ?? null;
  if (pair.refresh_token) refreshToken = pair.refresh_token;
  if (accessToken) scheduleRefresh(accessToken);
  persist();
  return accessToken ?? "";
}

/** Schedule the next refresh a safe margin before the access token expires. */
function scheduleRefresh(token: string): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  const expMs = readExpMs(token);
  const lifetime = expMs ? expMs - Date.now() : FALLBACK_LIFETIME_MS;
  const delay = Math.max(MIN_LIFETIME_MS, lifetime - REFRESH_LEAD_MS);
  refreshTimer = setTimeout(() => {
    void renewProdToken();
  }, delay);
}

/**
 * Renew the access token: try a refresh-token rotation first; on any failure
 * fall back to a full re-login with the demo credentials. Retries on total
 * failure with a short back-off, so a transient auth-service blip never leaves
 * the app permanently token-less. Concurrent callers share one in-flight renew.
 */
export function renewProdToken(): Promise<string | null> {
  // A deliberate logout must never be undone by a background renew/re-login.
  if (isManualLogout()) return Promise.resolve(accessToken);
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      // Only a refresh-token rotation keeps a session alive. We NEVER fall back
      // to a demo login here: silently turning a logged-in user (or a fresh
      // visitor) into the demo user is exactly the "re-logs-in as demo" bug.
      // No refresh token => end the session cleanly and let the render-gate show
      // the login screen. Demo is now opt-in only (loginAsDemo, via the button).
      if (!refreshToken) {
        accessToken = null;
        persist();
        return null;
      }
      const pair = await refreshOnce();
      return storePair(pair);
    } catch (err) {
      const status = err instanceof AuthError ? err.status : undefined;
      const rateLimited = status === 429;
      // A hard auth failure (bad/expired refresh token: 401/403) will never
      // recover by retrying — end the session so the gate shows the login
      // screen (no demo). Only TRANSIENT failures (network blip, 429, 5xx) are
      // worth a backed-off retry; those keep any previously-valid token.
      const transient = rateLimited || status === undefined || status >= 500;
      if (!transient) {
        accessToken = null;
        refreshToken = null;
        if (refreshTimer) {
          clearTimeout(refreshTimer);
          refreshTimer = null;
        }
        persist();
        return null;
      }
      if (typeof console !== "undefined") {
        console.warn(`[prodAuth] renew failed, retrying${rateLimited ? " (rate-limited)" : ""}`, err);
      }
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(
        () => {
          void renewProdToken();
        },
        rateLimited ? RATE_LIMIT_DELAY_MS : RETRY_DELAY_MS,
      );
      return accessToken;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Log the user out. Best-effort POST to `${authBase}/v1/auth/logout` (with
 * `credentials:'include'` so any auth cookie is sent) to revoke the session
 * server-side — network/HTTP failures are swallowed, since the local teardown
 * below is what actually ends the session for this SPA. Then: clear both
 * tokens, cancel the refresh timer, drop the persisted pair, and SET the
 * manual-logout flag so the app does NOT auto-relogin the demo user. The
 * render-gate then shows the login screen.
 */
export async function logoutProd(): Promise<void> {
  try {
    await fetch(logoutUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: refreshToken ? JSON.stringify({ refresh_token: refreshToken }) : undefined,
    });
  } catch {
    /* best-effort — local teardown below is authoritative for this SPA */
  }
  accessToken = null;
  refreshToken = null;
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  persist();
  setManualLogout(true);
}

/**
 * Initialize prod auth at app start. First restore any token pair persisted in
 * sessionStorage from an earlier page-load: if its access token is still fresh,
 * reuse it as-is (NO login call — this is what keeps a page reload from burning
 * a login and tripping the ~10/15min rate limit). Otherwise, if we hold a
 * refresh token, rotate it; only as a last resort do a full login. No-op unless
 * prod-auth is enabled. Never throws — a failed renew schedules a retry.
 *
 * If the user has manually logged out, SKIP the demo auto-login entirely and
 * leave the app token-less: the render-gate then shows the login screen.
 */
export async function initProdAuth(): Promise<void> {
  if (!isProdAuthEnabled()) return;
  if (isManualLogout()) {
    // Deliberate logout — do not restore or mint any token. Drop any stale
    // persisted pair so the gate sees a clean token-less state.
    accessToken = null;
    refreshToken = null;
    persist();
    return;
  }
  restorePersisted();
  if (isTokenFresh(accessToken)) {
    // Reuse the persisted token untouched; just arm the refresh timer.
    scheduleRefresh(accessToken as string);
    return;
  }
  // Stale/absent access token: rotate via refresh if we have one, else login.
  await renewProdToken();
}

/** Test-only: clear all in-memory state, timers, and the persisted pair. */
export function __resetProdAuthForTests(): void {
  accessToken = null;
  refreshToken = null;
  inFlight = null;
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  try {
    store()?.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    localStore()?.removeItem(MANUAL_LOGOUT_KEY);
  } catch {
    /* ignore */
  }
}
